import { LIGHT_GREY } from '@gcm/gcm-ui-angular';
import { ChangeDetectorRef, Component, OnInit, OnDestroy, ElementRef, effect, inject, input } from '@angular/core';
import { HighchartsService } from 'src/app/services/highcharts-service.service';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { GcmChartComponent } from '../gcm-chart/gcm-chart.component';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { DataSeries } from 'src/app/models/classes/data-series.class';
import { KeyValuePair } from 'src/app/models/classes/key-value-pair.class';

@Component({
  selector: 'app-base-geography-widget',
  imports: [BaseWidgetComponent, GcmChartComponent],
  templateUrl: './base-geography-widget.component.html'
})
export class BaseGeographyWidgetComponent implements OnInit, OnDestroy {
  private highchartsService = inject(HighchartsService);
  private currencyFormat = inject(CurrencyFormatPipe);
  private elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);

  private resizeObserver!: ResizeObserver;
  private isNarrow = false;
  private cachedUsEntries: { key: string; value: number }[] = [];
  private static readonly MAP_NARROW_WIDTH = 480;

  readonly widgets = input<Record<string, KeyValuePair<string | number, number>[]> | undefined>(undefined);
  readonly byText = input<string>('By Remaining Value');
  readonly reflowEvent = input<unknown>(undefined);
  readonly currency = input<string>('USD');
  readonly showUSMapWidget = input<boolean>(false);
  error = '';
  chartStyle = { width: '99%', display: 'flex' };
  remainingValueByRegionMap: Record<string, unknown>[] = [];
  remainingValueByRegionMapChartOptions: Highcharts.Options | null = null;
  continentData: { key: string; value: number; percentage: string }[] = [];
  remainingValuesForGeography: { continentKey: string; continentValue: number }[] = [];
  widgetData: KeyValuePair<string | number, number>[] = [];
  dataMode: 'world' | 'us-only' | 'mixed' = 'world';
  usMapData: Record<string, unknown>[] = [];
  usMapChartOptions: Highcharts.Options | null = null;
  pieData: DataSeries | null = null;
  pieChartOptions: Highcharts.Options | null = null;
  otherCountryNames = input<string[]>([]);

  ngOnInit(): void {
    this.resizeObserver = new ResizeObserver(entries => {
      const hostWidth = entries[0]?.contentRect.width ?? 0;
      // In mixed mode the US map occupies ~60% of the host; adjust accordingly
      const effectiveMapWidth = this.dataMode === 'mixed' ? hostWidth * 0.6 : hostWidth;
      const wasNarrow = this.isNarrow;
      this.isNarrow = effectiveMapWidth < BaseGeographyWidgetComponent.MAP_NARROW_WIDTH;
      if (wasNarrow !== this.isNarrow && this.cachedUsEntries.length > 0) {
        this.calculateUsMapData(this.cachedUsEntries);
        this.cdr.markForCheck();
      }
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private readonly rebuildGeography = effect(() => {
    const widgets = this.widgets();
    if (widgets && widgets['remainingValueByMapRegion']) {
      this.widgetData = widgets['remainingValueByMapRegion'];
      const usEntries = this.widgetData
        .filter(d => d.key.toString().startsWith('US - '))
        .map(d => ({ key: d.key.toString(), value: d.value }));

      if (this.showUSMapWidget() && usEntries.length > 0) {
        this.cachedUsEntries = usEntries;
        this.calculateUsMapData(usEntries);

        const countryData: { key: string; value: number }[] = (widgets['remainingValueByCountry'] ?? []) as { key: string; value: number }[];
        const countrySum = countryData.reduce((s, e) => s + e.value, 0);
        const usSum = usEntries.reduce((s: number, e: { value: number }) => s + e.value, 0);

        if (countrySum > 0 && usSum > 0) {
          this.calculatePieData(countryData, countrySum);
          this.dataMode = 'mixed';
        } else {
          this.pieData = null;
          this.pieChartOptions = null;
          this.dataMode = 'us-only';
        }
        return;
      }

      this.dataMode = 'world';
      this.usMapData = [];
      this.usMapChartOptions = null;
      this.pieData = null;
      this.pieChartOptions = null;
      this.otherCountryNames().length = 0;
      this.calculateValuesForGeographyChart();
    } else {
      this.dataMode = 'world';
      this.error = 'No Results';
    }
  });

  calculateValuesForGeographyChart() {
    if (this.widgetData && this.widgetData.length > 0) {
      this.error = '';
      this.remainingValuesForGeography = [];
      this.widgetData.map((obj) => {
        const value = obj.value;
        const key = obj.key
        this.remainingValuesForGeography.push({ continentKey: key.toString(), continentValue: value })
      });

      let remainingValuesForGeographySum = 0;
      this.remainingValuesForGeography.map(rvg => {
        remainingValuesForGeographySum += rvg.continentValue;
      });

      if (remainingValuesForGeographySum <= 0) {
        this.error = this.highchartsService.noResultsText;
        return;
      }

      let valueOfContinent, percentageForContinent;
      this.continentData = [];
      this.highchartsService.continentWithMapCodeMapping.forEach(ele => {
        if (remainingValuesForGeographySum > 0 && this.remainingValuesForGeography.filter(rvg => rvg.continentKey === ele.continent).length > 0) {
          valueOfContinent = this.remainingValuesForGeography.filter(rvg => rvg.continentKey === ele.continent).map(rvg => rvg.continentValue)[0];
          percentageForContinent = ((valueOfContinent / remainingValuesForGeographySum) * 100).toFixed(1)
          this.continentData.push({ key: ele.code, value: valueOfContinent, percentage: percentageForContinent })
        }
        else {
          this.continentData.push({ key: ele.code, value: 0, percentage: '0' })
        }
      });

      this.remainingValueByRegionMap = [];
      this.continentData.forEach(element => {
        if (element.percentage === '0') {
          if (['eu', 'as', 'na'].includes(element.key)) {
            this.remainingValueByRegionMap.push({ 'hc-key': element.key, key: element.key, displayName: element.key, value: element.percentage, actualValue: element.value, color: this.highchartsService.colorCodesMappingWithContinent.filter(x => x.code === 'noData').map(x => x.color)[0] })
          }
          else if (element.key === 'other') {
            ['oc', 'sa'].forEach(ele => {
              this.remainingValueByRegionMap.push({ 'hc-key': ele, key: ele, displayName: ele, value: element.percentage, actualValue: element.value, color: this.highchartsService.colorCodesMappingWithContinent.filter(x => x.code === 'noData').map(x => x.color)[0], dataLabels: { enabled: false } });
            });
            this.remainingValueByRegionMap.push({ 'hc-key': 'af', key: 'other', displayName: 'other', value: element.percentage, actualValue: element.value, color: this.highchartsService.colorCodesMappingWithContinent.filter(x => x.code === 'noData').map(x => x.color)[0] });
          }
        }
        else {
          if (element.key === 'other') {
            ['oc', 'sa'].forEach(ele => {
              this.remainingValueByRegionMap.push({ 'hc-key': ele, key: ele, displayName: ele, value: element.percentage, actualValue: element.value, color: this.highchartsService.colorCodesMappingWithContinent.filter(x => x.code === 'other').map(x => x.color)[0], dataLabels: { enabled: false } });
            });
            this.remainingValueByRegionMap.push({ 'hc-key': 'af', key: 'other', displayName: 'other', value: element.percentage, actualValue: element.value, color: this.highchartsService.colorCodesMappingWithContinent.filter(x => x.code === element.key).map(x => x.color)[0] });
          }
          else {
            this.remainingValueByRegionMap.push({ 'hc-key': element.key, key: element.key, displayName: element.key, value: element.percentage, actualValue: element.value, color: this.highchartsService.colorCodesMappingWithContinent.filter(x => x.code === element.key).map(x => x.color)[0] });
          }
        }
      });
      this.remainingValueByRegionMapChartOptions = this.highchartsService.getMapHighchartsOptions(this.remainingValueByRegionMap as Record<string, number>[], this.currency());
    }
    else {
      this.error = this.highchartsService.noResultsText;
    }
  }

  calculateUsMapData(usEntries: { key: string; value: number }[]): void {
    const total = usEntries.reduce((sum, w) => sum + w.value, 0);
    if (total <= 0) {
      this.usMapData = [];
      this.usMapChartOptions = null;
      this.error = this.highchartsService.noResultsText;
      return;
    }
    this.error = '';

    const noDataColor = this.highchartsService.colorCodesMappingWithContinent.find(x => x.code === 'noData')?.color ?? LIGHT_GREY;
    const regionData: Record<string, { value: number; percentage: string }> = {};
    usEntries.forEach(w => {
      const mapping = this.highchartsService.usDivisionMapping.find(m => m.division === w.key);
      if (mapping) {
        regionData[mapping.code] = {
          value: w.value,
          percentage: ((w.value / total) * 100).toFixed(1)
        };
      }
    });

    this.usMapData = this.highchartsService.usDivisionMapping.flatMap(d => {
      const rd = regionData[d.code];
      return d.states.map(state => {
        const isLabelState = d.labelState === state && !!rd;
        return {
          'hc-key': `us-${state}`,
          value: rd ? parseFloat(rd.percentage) : 0,
          actualValue: rd?.value ?? 0,
          regionName: d.division,
          color: rd ? d.color : noDataColor,
          displayLabel: isLabelState ? d.labelText : null,
          labelOffset: isLabelState ? (d.labelOffset ?? null) : null,
          dataLabels: { enabled: isLabelState }
        };
      });
    });

    this.usMapChartOptions = this.highchartsService.getUsMapHighchartsOptions(this.usMapData, this.currency(), BaseGeographyWidgetComponent.MAP_NARROW_WIDTH);
  }

  calculatePieData(
    countryValues: { key: string; value: number }[], sum: number
  ): void {
    this.error = '';
    if (sum <= 0) {
      this.pieData = null;
      this.pieChartOptions = null;
      return;
    }

    this.pieData = this.highchartsService.buildDataSeries(countryValues, 'Remaining Market Value');
    this.pieChartOptions = this.buildPieChartOptions(sum);
  }

  private buildPieChartOptions(sum: number): Highcharts.Options | null {
    if (sum <= 0) return null;
    const currencyFormat = this.currencyFormat;
    const currency = this.currency;
    const whiteColor = this.highchartsService.whiteColor;
    return {
      chart: { height: 340, type: 'pie' },
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: { fontWeight: 'normal', fontSize: '11px' },
        symbolRadius: 0,
        labelFormatter: function (this: Highcharts.Point | Highcharts.Series) {
          const pct = ((Math.abs((this as Highcharts.Point).y ?? 0) / sum) * 100).toFixed(1);
          return `<b>${pct}%</b> ${this.name}`;
        }
      },
      plotOptions: {
        pie: { size: '90%', innerSize: '65%', showInLegend: true },
        series: {
          dataLabels: { enabled: false },
          states: { hover: { halo: null } }
        }
      },
      tooltip: {
        formatter: function (this: Highcharts.Point) {
          const valToShow = currencyFormat.transform(this.y, currency());
          return `${this.series.name}<br/>${valToShow}<br/><hr class="mt-2 mb-2 mr-0 ml-0" style="background-color: ${whiteColor}"><div>${this.key}</div>`;
        }
      }
    } as Highcharts.Options;
  }
}
